import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ArrowLeft, FileText, Download } from 'lucide-react'
import { useState } from 'react'
import { getInvoices1Options } from '@/lib/client/@tanstack/react-query.gen'

export const Route = createFileRoute('/link/$parentId/invoices/')({
  component: InvoicesHistory,
})

function InvoicesHistory() {
  const { parentId } = Route.useParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')

  const { data: invoices, isLoading } = useQuery({
    ...getInvoices1Options({
      path: { parentId },
    }),
  })

  const filteredInvoices = invoices?.filter((invoice: any) => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'ALL' || invoice.status === statusFilter
    const isNotDraft = invoice.status !== 'DRAFT'
    return matchesSearch && matchesStatus && isNotDraft
  }) || []

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PAID': return 'Payée'
      case 'PARTIALLY_PAID': return 'Partiellement payée'
      case 'PENDING': return 'En attente'
      case 'FAILED': return 'Échec'
      case 'CANCELLED': return 'Annulée'
      default: return status
    }
  }

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'PAID': return 'default'
      case 'PARTIALLY_PAID': return 'secondary'
      case 'PENDING': return 'outline'
      case 'FAILED':
      case 'CANCELLED': return 'destructive'
      default: return 'outline'
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/link/$parentId" params={{ parentId }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au tableau de bord
            </Link>
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Historique des factures</h1>
          <p className="text-gray-600 mt-2">Consultez toutes vos factures et téléchargez les au format PDF</p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Rechercher par numéro de facture..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="w-full md:w-48">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tous les statuts</SelectItem>
                    <SelectItem value="PAID">Payées</SelectItem>
                    <SelectItem value="PARTIALLY_PAID">Partiellement payées</SelectItem>
                    <SelectItem value="PENDING">En attente</SelectItem>
                    <SelectItem value="FAILED">Échec</SelectItem>
                    <SelectItem value="CANCELLED">Annulées</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{filteredInvoices.length} facture(s)</CardTitle>
            <CardDescription>Historique complet de vos factures</CardDescription>
          </CardHeader>
          <CardContent>
            {filteredInvoices.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucune facture trouvée</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredInvoices.map((invoice: any) => (
                  <div
                    key={invoice.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-start space-x-4">
                        <FileText className="h-5 w-5 text-gray-400 mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{invoice.invoiceNumber}</h3>
                            <Badge variant={getStatusVariant(invoice.status)}>
                              {getStatusLabel(invoice.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">
                            Période : {new Date(invoice.billingPeriodStart).toLocaleDateString('fr-FR')} au{' '}
                            {new Date(invoice.billingPeriodEnd).toLocaleDateString('fr-FR')}
                          </p>
                          <p className="text-sm text-gray-500">
                            Échéance : {new Date(invoice.dueDate).toLocaleDateString('fr-FR')}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            Créée le {new Date(invoice.createdAt).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col md:items-end gap-2">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-gray-900">
                            {invoice.totalAmount.toFixed(2)}€
                          </p>
                          {invoice.paidAmount > 0 && (
                            <>
                              <p className="text-sm text-green-600">
                                {invoice.paidAmount.toFixed(2)}€ payés
                              </p>
                              {invoice.remainingAmount > 0 && (
                                <p className="text-sm text-orange-600">
                                  {invoice.remainingAmount.toFixed(2)}€ restants
                                </p>
                              )}
                            </>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <Button asChild size="sm" variant="outline">
                            <Link
                              to="/link/$parentId/invoices/$invoiceId"
                              params={{ parentId, invoiceId: invoice.id }}
                            >
                              Détails
                            </Link>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => window.open(`${import.meta.env.VITE_API_URL}/api/public/invoices/${invoice.id}/pdf`, '_blank')}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            PDF
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
